import { viewerStrings } from '../copy/strings.js';
import { message, optionalText } from '../copy/text.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { QueryField, QueryRecord, QueryScope } from '@cdr-clarity/contracts';
import {
  relatedRecords,
  type FindRelated,
  type RelatedPreview as Preview,
} from '../state/related-records.js';
import type { ExecuteQuery } from '../state/record-page.js';
import { RelatedPreview } from './RelatedPreview.js';
import { useHoverPin } from './useHoverPin.js';
import './related-records.css';

export interface CorrelatedRecordsOptions {
  find: FindRelated;
  execute: ExecuteQuery;
  scope: QueryScope;
  fields: readonly QueryField[];
  timezone: string;
  /** Change on navigation, source or field invalidation, including when pinned. */
  contextKey: string;
  focus(ids: readonly string[]): void;
  inspect(record: QueryRecord, collection: 'cdr' | 'cmr'): void;
  onError(message: string): void;
}
interface OpenPreview {
  data: Preview;
  sourceId: string;
  collection: 'cdr' | 'cmr';
  top: number;
  left: number;
  cursors: readonly (string | undefined)[];
  index: number;
}

/** Approved floating hover/pin interaction, supplied by the production shared service. */
export function useCorrelatedRecords(options: CorrelatedRecordsOptions) {
  const [preview, setPreview] = useState<OpenPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const popup = useRef<HTMLDivElement>(null);
  const anchor = useRef<HTMLElement | null>(null);
  const generation = useRef(0);
  const latest = useRef(options);
  latest.current = options;
  const dismiss = useCallback(() => {
    ++generation.current;
    setPreview(null);
    setBusy(false);
  }, []);
  const { pinned, pinnedRef, enter, leave, toggle, close, closeUnlessPinned } =
    useHoverPin(dismiss);
  const fieldKey = JSON.stringify(options.fields.map((field) => field.id));
  const scopeKey = JSON.stringify(options.scope);
  useEffect(close, [close, options.contextKey, fieldKey, scopeKey, options.timezone]);
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !popup.current?.contains(event.target) &&
        !anchor.current?.contains(event.target)
      )
        closeUnlessPinned();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    const scroll = (event: Event) => {
      if (!(event.target instanceof Node) || !popup.current?.contains(event.target))
        closeUnlessPinned();
    };
    const resize = () =>
      setPreview(
        (current) =>
          current && {
            ...current,
            left: scrollX + Math.max(12, Math.min(current.left - scrollX, innerWidth - 900)),
          },
      );
    document.addEventListener('pointerdown', outside);
    document.addEventListener('scroll', scroll, { capture: true, passive: true });
    window.addEventListener('keydown', key);
    window.addEventListener('resize', resize);
    window.addEventListener('hashchange', close);
    window.addEventListener('popstate', close);
    return () => {
      ++generation.current;
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('scroll', scroll, true);
      window.removeEventListener('keydown', key);
      window.removeEventListener('resize', resize);
      window.removeEventListener('hashchange', close);
      window.removeEventListener('popstate', close);
    };
  }, [close, closeUnlessPinned]);
  const load = useCallback(async (sourceId: string, collection: 'cdr' | 'cmr', cursor?: string) => {
    const { find, execute, scope, fields } = latest.current;
    return relatedRecords(find, execute, { ...scope, collection }, sourceId, fields, cursor);
  }, []);
  const open = useCallback(
    (
      record: QueryRecord,
      element: HTMLElement,
      collection: 'cdr' | 'cmr' = latest.current.scope.collection,
    ) => {
      enter();
      if (pinnedRef.current) return;
      anchor.current = element;
      const request = ++generation.current;
      void load(record.id, collection)
        .then((data) => {
          if (request !== generation.current || !element.isConnected) return;
          const rect = element.getBoundingClientRect();
          setPreview({
            data,
            sourceId: record.id,
            collection,
            top: scrollY + rect.bottom + 8,
            left: scrollX + Math.max(12, Math.min(rect.left, innerWidth - 900)),
            cursors: [undefined],
            index: 0,
          });
        })
        .catch((reason: unknown) => {
          if (request === generation.current)
            latest.current.onError(
              message(viewerStrings.webComponentsUseCorrelatedRecords.couldNotLoadRelatedRecords, {
                value1: String(reason),
              }),
            );
        });
    },
    [enter, pinnedRef, load],
  );
  function page(index: number): void {
    if (!preview || busy) return;
    const cursors = [...preview.cursors];
    if (index > preview.index && preview.data.result.nextCursor)
      cursors[index] = preview.data.result.nextCursor;
    const request = ++generation.current;
    setBusy(true);
    void load(preview.sourceId, preview.collection, cursors[index])
      .then((data) => {
        if (request === generation.current) setPreview({ ...preview, data, cursors, index });
      })
      .catch((reason: unknown) => {
        if (request === generation.current)
          latest.current.onError(
            message(viewerStrings.webComponentsUseCorrelatedRecords.couldNotLoadRelationshipPage, {
              value1: String(reason),
            }),
          );
      })
      .finally(() => {
        if (request === generation.current) setBusy(false);
      });
  }
  const renderIcon = useCallback(
    ({ data, collection }: { data?: QueryRecord; collection?: 'cdr' | 'cmr' }) =>
      data && (
        <button
          type="button"
          className="correlated-icon"
          aria-label={optionalText(
            viewerStrings.webComponentsUseCorrelatedRecords.exploreRelatedRecords,
          )}
          title={optionalText(
            viewerStrings.webComponentsUseCorrelatedRecords.exploreRelatedRecords2,
          )}
          onMouseEnter={(event) => open(data, event.currentTarget, collection)}
          onMouseLeave={leave}
          onFocus={(event) => open(data, event.currentTarget, collection)}
          onBlur={leave}
          onClick={(event) => {
            event.stopPropagation();
            open(data, event.currentTarget, collection);
          }}
          onDoubleClick={(event) => event.stopPropagation()}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M13,14C9.64,14 8.54,15.35 8.18,16.24C9.25,16.7 10,17.76 10,19A3,3 0 0,1 7,22A3,3 0 0,1 4,19C4,17.69 4.83,16.58 6,16.17V7.83C4.83,7.42 4,6.31 4,5A3,3 0 0,1 7,2A3,3 0 0,1 10,5C10,6.31 9.17,7.42 8,7.83V13.12C8.88,12.47 10.16,12 12,12C14.67,12 15.56,10.66 15.85,9.77C14.77,9.32 14,8.25 14,7A3,3 0 0,1 17,4A3,3 0 0,1 20,7C20,8.34 19.12,9.5 17.91,9.86C17.65,11.29 16.68,14 13,14M7,18A1,1 0 0,0 6,19A1,1 0 0,0 7,20A1,1 0 0,0 8,19A1,1 0 0,0 7,18M7,4A1,1 0 0,0 6,5A1,1 0 0,0 7,6A1,1 0 0,0 8,5A1,1 0 0,0 7,4M17,6A1,1 0 0,0 16,7A1,1 0 0,0 17,8A1,1 0 0,0 18,7A1,1 0 0,0 17,6Z" />
          </svg>
        </button>
      ),
    [open, leave],
  );
  return {
    renderIcon,
    open,
    close,
    popup:
      preview &&
      createPortal(
        <div
          className="floating-correlation"
          ref={popup}
          onMouseEnter={enter}
          onMouseLeave={leave}
          onFocus={enter}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) leave();
          }}
          style={{ top: preview.top, left: preview.left }}
        >
          <RelatedPreview
            preview={preview.data}
            sourceId={preview.sourceId}
            timezone={options.timezone}
            pinned={pinned}
            busy={busy}
            page={preview.index + 1}
            close={close}
            togglePin={toggle}
            previous={() => page(preview.index - 1)}
            next={() => page(preview.index + 1)}
            focus={(ids) => {
              close();
              latest.current.focus(ids);
            }}
            inspect={(record, collection) => {
              close();
              latest.current.inspect(record, collection);
            }}
          />
        </div>,
        document.body,
      ),
  };
}
